import { OmsOrderDetail } from 'types/oms';
import { Trans } from 'react-i18next';
import LOCALS from 'commons/locals';
import { Button, Card, InputNumber, message } from 'antd';
import { CircleDollarSign } from 'lucide-react';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { updateOrderLogisticsCost } from 'apis/oms';
import { CURRENCY_MAP } from 'constant/currency';
import { sumBy } from 'lodash-es';
import { ORDER_PAYMENT_STATUS_MAP } from 'constant/oms-order-payment-status';
import { getPointsDeductionActualCurrency, roundMoney } from 'utils/tools';

type OmsOrderPaymentInfoProps = {
  omsOrderDetail: OmsOrderDetail;
  logisticsCost?: number;
  freightWaiverReason?: 'DOMESTIC' | 'MEMBER' | null;
  onLogisticsCostSaved?: () => void;
};

const OmsOrderPaymentInfo = ({
  logisticsCost = 0,
  freightWaiverReason,
  onLogisticsCostSaved,
  omsOrderDetail: {
    id,
    totalAmount,
    payAmount,
    integrationAmount,
    totalTaxAmount,
    promotionAmount,
    promotionAmountActualCurrency,
    couponAmount,
    totalAmountActualCurrency,
    payAmountActualCurrency,
    freightAmountActualCurrency,
    payList,
    orderItemList: [{ actualCurrency, actualCurrencyRate }],
  },
}: OmsOrderPaymentInfoProps) => {
  const [draftCost, setDraftCost] = useState(logisticsCost);
  const [savingCost, setSavingCost] = useState(false);

  useEffect(() => {
    setDraftCost(logisticsCost);
  }, [logisticsCost]);

  const infoList = useMemo(() => {
    const pointsDeductionNode = (() => {
      const jpyPart = `-JPY ${integrationAmount.toLocaleString()}`;
      if (
        !integrationAmount ||
        !actualCurrency ||
        actualCurrency === CURRENCY_MAP.JPY
      ) {
        return jpyPart;
      }

      const convertedAmount = getPointsDeductionActualCurrency({
        totalAmountActualCurrency,
        promotionAmountActualCurrency,
        couponAmount,
        payAmountActualCurrency,
        actualCurrencyRate,
      });
      return `${jpyPart} (-${actualCurrency} ${convertedAmount.toLocaleString()})`;
    })();

    const list: {
      key: string;
      label: ReactNode;
      node: ReactNode;
    }[] = [
      {
        key: 'order_total_amount',
        label: <Trans i18nKey={LOCALS.order_total_amount} />,
        node:
          actualCurrency && totalAmountActualCurrency !== null
            ? `${actualCurrency} ${totalAmountActualCurrency.toLocaleString()}`
            : `JPY ${totalAmount.toLocaleString()}`,
      },
      {
        key: 'amount_to_be_paid',
        label: <Trans i18nKey={LOCALS.amount_to_be_paid} />,
        node:
          actualCurrency && payAmountActualCurrency !== null
            ? `${actualCurrency} ${payAmountActualCurrency.toLocaleString()}`
            : `JPY ${payAmount.toLocaleString()}`,
      },
      {
        key: 'tax_amount',
        label: <Trans i18nKey={LOCALS.tax_amount} />,
        node: `${actualCurrency} ${totalTaxAmount?.toLocaleString() || 0}`,
      },
      {
        key: 'points_deduction',
        label: <Trans i18nKey={LOCALS.points_deduction} />,
        node: pointsDeductionNode,
      },
    ];

    if (couponAmount) {
      list.push({
        key: 'coupon',
        label: <Trans i18nKey={LOCALS.coupon} />,
        node: `-JPY ${couponAmount}`,
      });
    }

    list.push({
      key: 'discount_amounts',
      label: <Trans i18nKey={LOCALS.discount_amounts} />,
      node:
        actualCurrency &&
        promotionAmountActualCurrency !== null &&
        promotionAmountActualCurrency !== undefined
          ? `-${actualCurrency} ${promotionAmountActualCurrency.toLocaleString()}`
          : `-JPY ${promotionAmount.toLocaleString()}`,
    });

    const freightAmount = freightAmountActualCurrency ?? 0;
    const freightWaiverText =
      freightAmount === 0 && freightWaiverReason
        ? ({
            DOMESTIC: <Trans i18nKey={LOCALS.freight_waiver_domestic} />,
            MEMBER: <Trans i18nKey={LOCALS.freight_waiver_member} />,
          }[freightWaiverReason] ?? freightWaiverReason)
        : null;

    list.push({
      key: 'freight',
      label: <Trans i18nKey={LOCALS.freight} />,
      node: (
        <div>
          <div>{`${actualCurrency} ${freightAmount.toLocaleString()}`}</div>
          {freightWaiverText ? (
            <div className="mt-0.5 text-xs font-normal text-gray-500">
              {freightWaiverText}
            </div>
          ) : null}
        </div>
      ),
    });

    // 找零金额：与小票打印一致 = 已支付金额合计 - 应付金额（加密货币支付时为 0）
    const receivedAmount =
      payList && payList.length
        ? sumBy(
            payList.filter(
              (i) => i.payStatus === ORDER_PAYMENT_STATUS_MAP.PAID,
            ),
            (i) => i.payAmount,
          )
        : payAmountActualCurrency || 0;
    const hasCryptoPayment = payList?.some(
      (item) => item.payType === 'CRYPTO_CURRENCY_TRIPLE_A',
    );
    const changeAmount = hasCryptoPayment
      ? 0
      : roundMoney(receivedAmount - (payAmountActualCurrency || 0));

    if (changeAmount > 0) {
      list.push({
        key: 'change',
        label: <Trans i18nKey={LOCALS.AYTHzloOiY} />,
        node: actualCurrency
          ? `${actualCurrency} ${changeAmount.toLocaleString()}`
          : `JPY ${changeAmount.toLocaleString()}`,
      });
    }

    return list;
  }, [
    actualCurrency,
    actualCurrencyRate,
    couponAmount,
    freightAmountActualCurrency,
    freightWaiverReason,
    integrationAmount,
    payAmount,
    payAmountActualCurrency,
    payList,
    promotionAmount,
    promotionAmountActualCurrency,
    totalAmount,
    totalAmountActualCurrency,
    totalTaxAmount,
  ]);

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex items-center gap-2 mb-4 text-base font-medium">
        <CircleDollarSign className="w-5 h-5" />
        <Trans i18nKey={LOCALS.payment_info} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-5">
        {infoList.map((item) => {
          return (
            <div key={item.key}>
              <div className="text-sm font-medium text-gray-500">
                {item.label}
              </div>
              <div className="mt-1 text-sm font-medium">{item.node}</div>
            </div>
          );
        })}
        <div>
          <div className="text-sm font-medium text-gray-500">
            物流成本（内部）
          </div>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-sm font-medium">{actualCurrency}</span>
            <InputNumber
              min={0}
              value={draftCost}
              onChange={(value) => setDraftCost(value ?? 0)}
            />
            <Button
              size="small"
              loading={savingCost}
              onClick={async () => {
                setSavingCost(true);
                try {
                  await updateOrderLogisticsCost(id, {
                    logisticsCost: draftCost ?? 0,
                  });
                  message.success('物流成本已更新');
                  onLogisticsCostSaved?.();
                } finally {
                  setSavingCost(false);
                }
              }}
            >
              保存
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default OmsOrderPaymentInfo;
